<template>
  <div class="headingbasicdetailsrules">
    <p class="notification">Tell us the basic details of your travel policy.</p>
    <div class="columncstm">
      <b-field class="mb-4" label="Travel Policy Name">
        <b-input type="text" required validation-message="Please enter a name for this travel policy"
          v-model="basicDetailsObj.ruleClass" @input="getBasicDetails" />
      </b-field>
    </div>
    <h1 class="mt-5">Payment Methods</h1>
    <div class="notification">
      Ensure you allow personal payment methods or specify existing corporate
      ghost cards, or both. 
    </div>

    <b-field class="mb-4" label="Allow personal payment methods?">
      <b-checkbox type="is-teal" @input="getBasicDetails" v-model="basicDetailsObj.allowPersonalPaymentMethods">
        <p>Yes</p>
      </b-checkbox>
    </b-field>

    <!-- Show single card selector (current default behavior) -->
    <div class="columncstm" v-if="shouldShowSingleCardSelector">
      <b-field class="mb-4" label="Corporate Ghost Card">
        <b-select placeholder="Select Corporate Ghost Card" v-model="basicDetailsObj.corporateGhostCard"
          @input="getBasicDetails" expanded>
          <option value="">--None--</option>
          <option v-for="option in cardOptions" :value="option" :key="option.id">
            {{ option.brand }} Card ending in {{ option.last4 }}
          </option>
        </b-select>
      </b-field>
    </div>
    <!-- Show multiple card selector for new policies or those migrated to new system -->

    <div v-else>
      <MultipleGhostCardsSelectBox v-model="basicDetailsObj.paymentAssociations" :cardOptions="cardOptions"
        @input="getBasicDetails" />
    </div>


    <h1 class="mt-5">Communication</h1>
    <b-field class="mb-4" label="Email address to notify of all bookings for travelers assigned to this policy">
      <div class="columncstm">
        <b-input type="email" v-model="basicDetailsObj.ccEmail" @input="getBasicDetails" />
      </div>
    </b-field>
    <h1 class="mt-5">Flight Search options</h1>
    <b-field class="mb-4" label="Do you want to allow travelers to book basic economy fares?"></b-field>
    <b-checkbox type="is-teal" true-value="Yes" false-value="No" v-model="basicDetailsObj.showBasicEconomyFares"
      @input="getBasicDetails">
      <p>Yes</p>
    </b-checkbox>

    <b-field class="mb-4" label="Which cabin classes should be available in the flight search form?"></b-field>
    <!-- <div class="notification">If you leave all these values unchecked, travelers will see all of them.</div> -->
    <b-checkbox type="is-teal" @input="getBasicDetails" native-value="Y" v-model="basicDetailsObj.cabinOptions">
      <p>Economy</p>
    </b-checkbox><br />
    <b-checkbox type="is-teal" @input="getBasicDetails" native-value="M" v-model="basicDetailsObj.cabinOptions">
      <p>Standard Economy</p>
    </b-checkbox><br />
    <b-checkbox type="is-teal" @input="getBasicDetails" native-value="W" v-model="basicDetailsObj.cabinOptions">
      <p>Premium Economy</p>
    </b-checkbox><br />
    <b-checkbox type="is-teal" @input="getBasicDetails" native-value="C" v-model="basicDetailsObj.cabinOptions">
      <p>Business</p>
    </b-checkbox><br />
    <b-checkbox type="is-teal" @input="getBasicDetails" native-value="F" v-model="basicDetailsObj.cabinOptions">
      <p>First</p>
    </b-checkbox>

    <b-field class="mb-4" label="Allow travelers to select paid seats for corporate travel?">
      <b-tooltip label="This value will bypass configuration set at Account Level" position="is-right">
        <b-radio v-model="basicDetailsObj.selectPaidSeats" @input="getBasicDetails" native-value="Allow">
          Allow
        </b-radio>
        <b-radio v-model="basicDetailsObj.selectPaidSeats" @input="getBasicDetails" native-value="Disallow">
          Disallow
        </b-radio>
      </b-tooltip>
    </b-field>

    <template v-if="allowWebBedsConfig">
      <h1 class="mt-5">Hotel Search options</h1>
      <b-field class="mb-4" label="Enable WebBeds hotel content to travelers?">
        <b-radio v-model="basicDetailsObj.enableWebBedsHotelContent" @input="getBasicDetails" :native-value="true">
          Yes
        </b-radio>
        <b-radio v-model="basicDetailsObj.enableWebBedsHotelContent" @input="getBasicDetails" :native-value="false">
          No
        </b-radio>
      </b-field>
    </template>
  </div>
</template>
<script>
import MultipleGhostCardsSelectBox from "../UI/MultipleGhostCardsSelectBox.vue";
export default {
  name: "BasicDetails",
  components: {MultipleGhostCardsSelectBox},
  props: {
    travelPolicy: {
      type: [Object],
    },
    cardOptions: {
      type: Array,
    },
    allowWebBedsConfig : Boolean
  },
  data() {
    return {
      basicDetailsObj: {
        ruleClass: "",
        corporateGhostCard: "",
        allowPersonalPaymentMethods: true,
        ccEmail: "",
        showBasicEconomyFares: "Yes",
        cabinOptions: ["Y", "M", "W"],
        selectPaidSeats: "",
        paymentAssociations: [],
        enableWebBedsHotelContent: false
      },
    };
  },
  computed: {
    shouldShowSingleCardSelector() {
      // Use multiple selector for new policies
      // For policies with corp ghost card but no paymentAssociations, show single selector
      // This maintains current behavior while allowing future migration
      return this.travelPolicy?.corporateGhostCard && (!this.travelPolicy?.paymentAssociations ||
             this.travelPolicy.paymentAssociations.length === 0);
    }
  },
  methods: {
    getBasicDetails() {
      this.$emit("basicDetails", this.basicDetailsObj);
    },
  },
  mounted() {
    this.$emit("basicDetails", this.basicDetailsObj);
  },
  watch: {
  travelPolicy: {
    handler(newVal) {
      this.basicDetailsObj.ruleClass = newVal.ruleClass;

      // Find matching card in cardOptions by ID to ensure proper selection
      if (newVal.corporateGhostCard && newVal.corporateGhostCard.id) {
        const matchingCard = this.cardOptions.find(card => card.id === newVal.corporateGhostCard.id);
        this.basicDetailsObj.corporateGhostCard = matchingCard || newVal.corporateGhostCard;
      } else {
        this.basicDetailsObj.corporateGhostCard = newVal.corporateGhostCard;
      }

      this.basicDetailsObj.allowPersonalPaymentMethods = newVal.allowPersonalPaymentMethods;
      this.basicDetailsObj.ccEmail = newVal.ccEmail;
      this.basicDetailsObj.showBasicEconomyFares = newVal.showBasicEconomyFares;
      this.basicDetailsObj.cabinOptions = newVal.cabinOptions;
      this.basicDetailsObj.selectPaidSeats = newVal.selectPaidSeats;
      this.basicDetailsObj.enableWebBedsHotelContent = newVal.enableWebBedsHotelContent;
      // 👇 Match references
      this.basicDetailsObj.paymentAssociations = newVal.paymentAssociations

      this.$emit("basicDetails", this.basicDetailsObj);
    },
    deep: true,
  },
},

};
</script>
